import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DEFAULT_THING_ID } from '@kemu-io/kemu-core/common/constants';
import { extractScriptMetadata } from '@kemu-io/kemu-core/widgets/script/index.js';
import { ScriptWidgetState } from '@kemu-io/kemu-core/widgets/script/types.js';
import Editor from 'react-simple-code-editor';
import { pasteFromClipboardAction } from '../reducers/clipboardReducers';
import styles from './ThirdPartyIntegrationCreationAssistant.module.css';
import { selectSignedUserProfile } from '@src/app/reducers/user/userSlice';
import platformApi from '@src/api/platform/platformApi';
import { useTranslation } from '@hooks/index';
import RoundedModal from '@components/roundedModal/roundedModal';
import { selectCurrentRecipe } from '@src/features/Workspace/workspaceSlice';
import { showGlobalNotification, showPremiumUpgradeModal } from '@src/features/interface/interfaceSlice';
import { decodeServerErrorInfo } from '@src/app/reducers/widget/utils';
import { SUBSCRIPTION_LIMIT_REACHED } from '@common/constants';
import { selectShowCreateIntegrationModal, showCreateIntegrationModalAction } from '../logicMapperSlice';

/**
 * Removes markdown code block wrappers from a string.
 * @param script - The string with potential markdown wrappers.
 * @returns The script string without markdown wrappers.
 */
const removeMarkdownWrappers = (script: string): string => {
  // Remove starting ```optional_language and closing ```
  const regex = /^```(?:[a-zA-Z0-9]+)?\n?([\s\S]*?)\n?```$/;
  const match = script.match(regex);
  if (match && match[1]) {
    return match[1].trim();
  }
  // If no match, or if the capturing group is empty, return the original string trimmed
  return script.trim();
};

/**
 * Escapes HTML special characters to prevent HTML injection in highlighted output.
 * @param value - Raw text to escape.
 * @returns Escaped text safe for rendering in a highlight string.
 */
const escapeHtml = (value: string): string => value
  .replace(/&/g, '&amp;')
  .replace(/</g, '&lt;')
  .replace(/>/g, '&gt;');

/**
 * Highlights URLs in a plain text string by wrapping them in a span, similar to markdown code highlighting.
 * @param value - The editor text content.
 * @returns HTML string with URL matches wrapped in a span.
 */
const highlightUrls = (value: string): string => {
  const escaped = escapeHtml(value);
  const urlRegex = /(\bhttps?:\/\/[^\s<>()]+|\bwww\.[^\s<>()]+)/g;

  return escaped.replace(urlRegex, (match) => {
    // Avoid highlighting trailing punctuation that commonly follows URLs in prose.
    const suffixMatch = match.match(/[),.!?\]\}]+$/);
    const suffix = suffixMatch ? suffixMatch[0] : '';
    const url = suffix ? match.slice(0, -suffix.length) : match;
    return `<span class="url-highlight">${url}</span>${suffix}`;
  });
};

const SCRIPT_TEMPLATE = {
  '{#}id-001': {
    'id': '{#}id-001',
    'canvas': {
      'position': {
        'x': 953,
        'y': 210
      }
    },
    'children': [],
    'state': {
      'pauseExecution': false,
      'autoPauseOnError': true,
      'metadata': {
        'name': 'My Script',
        'description': 'This is a test script.'
      },
      'pages': {
        'main': {
          'code': '--code here--',
          'language': 'javascript',
          'name': 'My First Script'
        }
      }
    },
    'type': 'script'
  }
};

/**
 * Modal component for generating third-party integration scripts using AI assistance
 */
const ThirdPartyIntegrationCreationAssistant = () => {
  const [prompt, setPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const currentUser = useSelector(selectSignedUserProfile);
  const currentRecipe = useSelector(selectCurrentRecipe);
  const showModal = useSelector(selectShowCreateIntegrationModal);
  const dispatch = useDispatch();
  const t = useTranslation();
  const wpT = useMemo(() => t.withBaseKey('LogicMapper.ThirdPartyIntegrationCreationAssistant'), [t]);

  /**
   * Handles the generated script by injecting it into the template and pasting to canvas
   * @param generatedScript - The script code generated by the AI
   */
  const generateScript = useCallback((generatedScript: string) => {
    const cleanedScript = removeMarkdownWrappers(generatedScript);
    const templateCopy = JSON.parse(JSON.stringify(SCRIPT_TEMPLATE));
    templateCopy['{#}id-001'].state.pages.main.code = cleanedScript;

    // process metadata created by the AI (if any)
    const metadata = extractScriptMetadata(cleanedScript);
    if (metadata) {
      (templateCopy['{#}id-001'].state as ScriptWidgetState).metadata = {
        ...(templateCopy['{#}id-001'].state as ScriptWidgetState).metadata,
        ...metadata,
      };
    }

    if (currentRecipe.poolId) {
      dispatch(pasteFromClipboardAction({
        recipeId: currentRecipe.poolId,
        thingId: DEFAULT_THING_ID,
        mapData: JSON.stringify(templateCopy),
      }));

      dispatch(showGlobalNotification({
        type: 'success',
        code: 'integration-creation',
        message: wpT('ScriptGenerated'),
      }));
    }
  }, [wpT, currentRecipe, dispatch]);

  /**
   * Handles the script generation request
   */
  const handleGenerate = useCallback(async () => {
    if (!prompt.trim()) {
      dispatch(showGlobalNotification({
        type: 'warning',
        code: 'integration-creation',
        title: wpT('GenerationError'),
        message: wpT('EmptyPromptWarning'),
      }));
      return;
    }

    if (!currentUser.profile?.id) {
      return;
    }

    setIsGenerating(true);
    let accumulatedResponse = '';

    try {
      await platformApi.integrationGen({
        prompt: prompt,
        userId: currentUser.profile.id,
        onSession: () => {
          // We don't need to track session for this simple use case
        },
        onChunk: (chunk) => {
          accumulatedResponse += chunk;
        },
      });

      // Call the generateScript function with the accumulated response
      generateScript(accumulatedResponse);

      // Close the modal after successful generation
      dispatch(showCreateIntegrationModalAction(false));
      setPrompt('');

    } catch (error) {
      console.error('Error generating integration script:', error);
      const { errorCode, message: errMessage } = decodeServerErrorInfo(error);

      if(errorCode === SUBSCRIPTION_LIMIT_REACHED){
        dispatch(showPremiumUpgradeModal(true));
      }
      dispatch(showGlobalNotification({
        type: 'error',
        code: 'integration-creation',
        title: wpT('GenerationError'),
        message: errMessage,
      }));
    } finally {
      setIsGenerating(false);
    }
  }, [prompt, wpT, currentUser, dispatch, generateScript]);

  /**
   * Handles modal cancellation
   */
  const handleCancel = useCallback(() => {
    if (!isGenerating) {
      setPrompt('');
      dispatch(showCreateIntegrationModalAction(false));
    }
  }, [isGenerating, dispatch]);

  /**
   * Handles Enter key press in the textarea
   */
  const handleKeyPress = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement | HTMLDivElement>) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !isGenerating) {
      e.preventDefault();
      handleGenerate();
    }
  }, [handleGenerate, isGenerating]);

  return (
    <RoundedModal
      title={wpT('Title')}
      visible={showModal}
      onCancel={handleCancel}
      className={styles.IntegrationCreationModalRoot}
      okBtnLabel={wpT('GenerateBtn')}
      cancelBtnLabel={wpT('CancelBtn')}
      onOk={handleGenerate}
      centered
      loadingOkBtn={isGenerating}
      disableOkButton={!prompt.trim() || isGenerating}
      disableCancelButton={isGenerating}
      closeOnMaskClick={false}
      closable={!isGenerating}
    >
      <div className={styles.Content}>
        <div className={styles.Description}>
          {wpT('Description')}
        </div>

        <div className={styles.InputGroup}>
          <label className={styles.Label}>{wpT('PromptLabel')}</label>
          <div className={styles.PromptInput}>
            <Editor
              value={prompt}
              onValueChange={setPrompt}
              highlight={highlightUrls}
              padding={12}
              placeholder={wpT('PromptPlaceholder')}
              disabled={isGenerating}
              onKeyDown={handleKeyPress}
              style={{
                minHeight: 110, // roughly 4 rows with padding
                fontFamily: 'Nunito',
                fontSize: 14,
                lineHeight: 1.5715,
                color: 'var(--kemu-text-primary)',
                background: 'transparent',
              }}
            />
          </div>
        </div>
      </div>
    </RoundedModal>
  );
};

export default ThirdPartyIntegrationCreationAssistant;

